import { forwardRef, memo } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';
import { cn } from '../../lib/styles';

const iconButtonVariants = cva(
  'flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: [
          'text-white fill-white',
          'bg-bg-inter-primary-default hover:bg-bg-inter-primary-hover active:bg-bg-inter-primary-pressed',
          'rounded-sm border border-bg-inter-primary-default hover:border-bg-inter-primary-hover active:border-bg-inter-primary-pressed',
          'disabled:bg-transparent disabled:border-border-default disabled:text-text-disabled disabled:fill-icon-disabled',
        ].join(' '),
        secondary:
          'rounded-sm text-icon-primary fill-icon-primary bg-bg-inter-secondary-default hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed disabled:text-icon-disabled disabled:hover:bg-bg-inter-secondary-default',
        tertiary: [
          'rounded-[100px]',
          'disabled:text-icon-disabled disabled:fill-icon-disabled disabled:hover:bg-transparent',
          'dark:text-white dark:fill-white',
          'dark:hover:bg-white/30 dark:active:bg-white/40',
        ].join(' '),
        outline:
          'border border-border-default rounded-sm text-icon-primary active:bg-bg-inter-tertiary-pressed hover:bg-bg-inter-tertiary-hover disabled:text-icon-disabled disabled:hover:bg-transparent',
      },
      color: {
        default: '',
        destructive: '',
      },
      size: {
        md: 'h-8 w-8 p-1',
        sm: 'h-6 w-6 p-1',
      },
      nestedFillCurrent: {
        true: '*:fill-current',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'tertiary',
        color: 'default',
        className: [
          'text-icon-primary fill-icon-primary',
          'active:bg-bg-inter-tertiary-pressed hover:bg-bg-inter-tertiary-hover',
        ].join(' '),
      },
      {
        variant: 'tertiary',
        color: 'destructive',
        className: [
          'text-text-destructive fill-text-destructive',
          'hover:bg-bg-inter-destructive-hover focus-visible:bg-bg-inter-destructive-pressed',
          'active:bg-bg-inter-destructive-pressed',
          'disabled:text-text-destructive/50 disabled:fill-text-destructive/50 disabled:hover:bg-transparent',
        ].join(' '),
      },
    ],
    defaultVariants: {
      variant: 'tertiary',
      color: 'default',
      size: 'md',
      nestedFillCurrent: true,
    },
  },
);

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>,
    VariantProps<typeof iconButtonVariants> {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  tooltipProps?: {
    withinPortal?: boolean;
  };
  active?: boolean;
  element?: React.ElementType;
  /**
   * The color scheme of the button.
   * Currently only works with `tertiary` variant.
   */
  color?: 'default' | 'destructive';
  /** Whether to apply fill current to all paths of the SVG */
  nestedFillCurrent?: boolean;
}

const IconButtonBase = (
  {
    children,
    tooltip,
    className,
    disabled,
    tooltipProps = {
      withinPortal: true,
    },
    variant,
    color,
    size,
    nestedFillCurrent = true,
    active,
    element = 'button',
    type = 'button',
    ...rest
  }: IconButtonProps & VariantProps<typeof iconButtonVariants>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const Comp: React.ElementType = element;

  const content = (
    <Comp
      ref={ref}
      className={cn(
        iconButtonVariants({ variant, color, size, nestedFillCurrent }),
        {
          'bg-bg-inter-tertiary-pressed': active,
        },
        className,
      )}
      type={type}
      disabled={disabled}
      {...rest}
    >
      {children}
    </Comp>
  );

  if (!tooltip) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export const IconButton = memo(forwardRef(IconButtonBase));
